// ============= Imports =============
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Accordion,
  SearchBarWithDropdown,
  Spacer,
} from '@tw/tw-component-library';
import { Button } from '@tw/tw-component-library/common-wt';
import { useDefaultSearch } from '../../container/forms/hooks/useUnifiedSearch';
import { Suggestion } from '../../container/forms/types/common/common.types';
import { UseAuth } from '../../../contexts/AuthContext';
import { createDebounce } from '../../../utils/debounce';
import { canSpoof } from '../../../utils/permissions';
import { getWorkflowKeyFromPath } from '../../../utils/routeUtils';
import { getOfficeForWorkflowKey } from '../../../utils/workflowRegistry';
import {
  AdminHeaderContainer,
  AdminHeaderWrapper,
  SpoofContainer,
  SpoofWrapper,
  SpoofNotice,
} from './AdminHeader.styles';

// ============= Component =============
export const AdminHeader: React.FC = () => {
  const { setSpoofedUser, spoofedUser, originalUser, currentUser } = UseAuth();
  const location = useLocation();
  const [searchValue, setSearchValue] = useState('');
  const [options, setOptions] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lastSuggestions, setLastSuggestions] = useState<Suggestion[]>([]);

  const office = useMemo(() => {
    const workflowKey = getWorkflowKeyFromPath(location.pathname);
    if (!workflowKey) {
      return '';
    }
    return getOfficeForWorkflowKey(workflowKey) ?? '';
  }, [location.pathname]);

  const { handleTagPickerSearch } = useDefaultSearch<{
    selectedUser?: Suggestion;
    email?: string;
  }>({
    setTagPickerSuggestions: (suggestions: Suggestion[]) => {
      setLastSuggestions(suggestions);
      setOptions(suggestions.map((s) => s.label));
    },
    office,
  });

  const lastUserSearchRef = useRef<string>('');

  useEffect(() => {
    lastUserSearchRef.current = '';
  }, [office]);

  const debouncedUserSearch = useCallback(
    (value: string) => {
      const debouncedFn = createDebounce((searchValue: string) => {
        // If the input matches the currently selected spoofed user label, do not re-open
        if (spoofedUser?.label && searchValue === spoofedUser.label) {
          setIsOpen(false);
          setIsLoading(false);
          setOptions([]);
          return;
        }
        if (lastUserSearchRef.current === searchValue) return;
        lastUserSearchRef.current = searchValue;
        if (!searchValue.trim() || searchValue.trim().length < 3) {
          setIsOpen(false);
          setIsLoading(false);
          setOptions([]);
          return;
        }
        setIsLoading(true);
        void handleTagPickerSearch(searchValue)
          .then((results) => {
            setIsOpen((results?.length ?? 0) > 0);
          })
          .finally(() => {
            setIsLoading(false);
          });
      }, 300);
      debouncedFn(value);
    },
    [handleTagPickerSearch, spoofedUser?.label]
  );

  const handleSearch = useCallback(
    (value: string) => {
      setSearchValue(value);
      // If value equals the selected spoofed user's label, keep dropdown closed
      if (spoofedUser?.label && value === spoofedUser.label) {
        setIsOpen(false);
        setIsLoading(false);
        setOptions([]);
        setLastSuggestions([]);
        return;
      }
      if (!value.trim()) {
        setIsOpen(false);
        setIsLoading(false);
        setOptions([]);
        setLastSuggestions([]);
        if (setSpoofedUser) setSpoofedUser(null);
        return;
      }
      debouncedUserSearch(value);
    },
    [debouncedUserSearch, setSpoofedUser, spoofedUser?.label]
  );

  const handleSelect = useCallback(
    (selectedLabel: string | null) => {
      if (!selectedLabel) {
        setSearchValue('');
        setIsOpen(false);
        if (setSpoofedUser) setSpoofedUser(null);
        return;
      }
      const match = lastSuggestions.find((s) => s.label === selectedLabel);
      if (match && setSpoofedUser) {
        setSpoofedUser({
          label: match.label,
          value: match.value,
          email: match.email,
        });
        setSearchValue(match.label);
      }
      // Fully close and clear dropdown state after selection
      setIsOpen(false);
      setOptions([]);
      setLastSuggestions([]);
    },
    [lastSuggestions, setSpoofedUser]
  );

  // Keep search field in sync with persisted spoofed user
  useEffect(() => {
    if (spoofedUser?.label) {
      setSearchValue(spoofedUser.label);
    }
  }, [spoofedUser?.label]);

  const handleClearSpoofAndRefresh = () => {
    localStorage.removeItem('spoofedUser');
    window.location.reload();
  };

  // Check if the ORIGINAL user (not spoofed) has admin permissions
  // Fallback to currentUser if originalUser is not available (shouldn't happen in normal flow)
  const isCurrentUserAdmin = canSpoof(originalUser || currentUser);

  if (!isCurrentUserAdmin) {
    return null;
  }

  // Create dynamic title based on spoofing status
  const accordionTitle = spoofedUser
    ? `Admin Tools - Currently spoofing: ${spoofedUser.label}`
    : 'Admin Tools';

  return (
    <Accordion title={accordionTitle} extras={{ defaultExpanded: false }}>
      <AdminHeaderWrapper>
        <AdminHeaderContainer>
          <SpoofContainer>
            {!spoofedUser && (
              <SpoofNotice>
                <strong>Admin Status:</strong> You are logged in as an admin
                user. Use the search below to spoof as another user for testing
                purposes.
                <br />
                <em>
                  Current User: {originalUser?.userDisplayName || 'Unknown'}
                </em>
              </SpoofNotice>
            )}
            <SpoofWrapper>
              <SearchBarWithDropdown<string>
                options={options}
                placeholder="Spoof user..."
                onSelect={handleSelect}
                onSearch={handleSearch}
                value={searchValue}
                searchButton={false}
                getDisplayText={(option: string) => option}
                isOpenOverride={isOpen}
                setIsOpenOverride={setIsOpen}
                isLoading={isLoading}
              />
            </SpoofWrapper>
            {spoofedUser && (
              <SpoofNotice>
                <strong>Currently Spoofing:</strong> {spoofedUser.label}
                <br />
                <strong>Admin Note:</strong> You are viewing as a non-admin
                user. Admin tools are still available to you, but the spoofed
                user would not see them.
                <br />
                <em>
                  Your Admin Identity:{' '}
                  {originalUser?.userDisplayName || 'Unknown'}
                </em>
              </SpoofNotice>
            )}
            <Spacer />
            <Button
              size="sm"
              variant="secondary"
              onDark
              onClick={handleClearSpoofAndRefresh}
              aria-label="Clear spoofed user and refresh page"
            >
              Clear Spoof & Refresh
            </Button>
          </SpoofContainer>
        </AdminHeaderContainer>
      </AdminHeaderWrapper>
    </Accordion>
  );
};

// ============= PropTypes =============
AdminHeader.displayName = 'AdminHeader';
